import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSalesHeaders } from '../../utils/salesAuth';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;
const LEAD_SOURCES = ['website','instant_estimate','ai_chat','google','google_business_profile','facebook','facebook_marketplace','referral','repeat_customer','phone','email','manual','other'];
const PROJECT_TYPES = ['pipe_fence','chain_link_fence','iron_fence','wood_fence','concrete','metal_shop','welding_repair','trailer_repair','custom_fabrication','gate','other'];

export default function AddLead() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [estimateFile, setEstimateFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [form, setForm] = useState({
    name: '', first_name: '', last_name: '', email: '', phone: '',
    address: '', city: '', state: 'TX', zip_code: '', company: '',
    project_type: '', message: '', services: [],
    estimated_value: '', lead_source: 'manual', priority: 'medium', tags: '',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleService = (svc) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(svc) ? prev.services.filter(s => s !== svc) : [...prev.services, svc],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.first_name.trim()) { setError('Name is required'); return; }
    if (!form.phone.trim() && !form.email.trim()) { setError('Phone or email is required'); return; }
    setError('');
    setSaving(true);
    try {
      const body = {
        ...form,
        name: form.name || `${form.first_name} ${form.last_name}`.trim(),
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      const res = await fetch(`${API}/api/sales/leads`, {
        method: 'POST',
        credentials: 'include',
        headers: getSalesHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed to add lead'); }
      const data = await res.json();

      // If estimate file attached, upload and extract
      if (estimateFile) {
        setExtracting(true);
        try {
          const fd = new FormData();
          fd.append('file', estimateFile);
          fd.append('customer_id', data.customer?.id || '');
          fd.append('opportunity_id', data.opportunity?.id || '');
          await fetch(`${API}/api/sales/leads/extract-estimate`, {
            method: 'POST',
            credentials: 'include',
            body: fd,
          });
        } catch (e) {
          console.error('Estimate extraction failed:', e);
        } finally {
          setExtracting(false);
        }
      }

      navigate(`/sales/opportunity/${data.opportunity.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 };

  return (
    <div data-testid="add-lead-page" style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: isMobile ? '10px 12px' : '14px 28px', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14 }}>
        <button data-testid="add-lead-back" onClick={() => navigate('/sales')} style={{ padding: '8px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13, minHeight: 40 }}>Back</button>
        <h1 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>Add New Lead</h1>
      </div>

      <div style={{ maxWidth: 720, margin: isMobile ? '12px auto' : '24px auto', padding: isMobile ? '0 12px' : '0 20px' }}>
        {error && <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <form onSubmit={submit}>
          {/* Contact Info */}
          <div style={{ background: '#fff', borderRadius: 12, padding: isMobile ? '16px 14px' : '20px 24px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Contact Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
              <div><label style={labelStyle}>First Name</label><input data-testid="lead-first-name" value={form.first_name} onChange={e => set('first_name', e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Last Name</label><input data-testid="lead-last-name" value={form.last_name} onChange={e => set('last_name', e.target.value)} style={inputStyle} /></div>
              <div style={{ gridColumn: isMobile ? undefined : 'span 2' }}><label style={labelStyle}>Full Name (or Business Name)</label><input data-testid="lead-name" value={form.name} onChange={e => set('name', e.target.value)} style={inputStyle} placeholder="Leave blank to auto-fill from first/last" /></div>
              <div><label style={labelStyle}>Phone *</label><input data-testid="lead-phone" value={form.phone} onChange={e => set('phone', e.target.value)} style={inputStyle} placeholder="817-555-1234" /></div>
              <div><label style={labelStyle}>Email</label><input data-testid="lead-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Company</label><input value={form.company} onChange={e => set('company', e.target.value)} style={inputStyle} /></div>
            </div>
          </div>

          {/* Address */}
          <div style={{ background: '#fff', borderRadius: 12, padding: isMobile ? '16px 14px' : '20px 24px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Address</h3>
            <div style={{ marginBottom: 12 }}><label style={labelStyle}>Street Address</label><input data-testid="lead-address" value={form.address} onChange={e => set('address', e.target.value)} style={inputStyle} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 80px 120px', gap: 14 }}>
              <div><label style={labelStyle}>City</label><input value={form.city} onChange={e => set('city', e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>State</label><input value={form.state} onChange={e => set('state', e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>ZIP</label><input value={form.zip_code} onChange={e => set('zip_code', e.target.value)} style={inputStyle} /></div>
            </div>
          </div>

          {/* Project Info */}
          <div style={{ background: '#fff', borderRadius: 12, padding: isMobile ? '16px 14px' : '20px 24px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>Project Details</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Project Type</label>
              <select data-testid="lead-project-type" value={form.project_type} onChange={e => set('project_type', e.target.value)} style={inputStyle}>
                <option value="">Select...</option>
                {PROJECT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Services</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PROJECT_TYPES.map(svc => (
                  <button key={svc} type="button" onClick={() => toggleService(svc)}
                    style={{ padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: form.services.includes(svc) ? 'none' : '1px solid #d1d5db', background: form.services.includes(svc) ? '#c0392b' : '#fff', color: form.services.includes(svc) ? '#fff' : '#475569', minHeight: 36 }}>
                    {svc.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}><label style={labelStyle}>Notes / Message</label><textarea data-testid="lead-message" value={form.message} onChange={e => set('message', e.target.value)} rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle}>Estimated Value ($)</label>
                <input type="number" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} style={inputStyle} placeholder="5000" />
              </div>
              <div>
                <label style={labelStyle}>Lead Source</label>
                <select data-testid="lead-source" value={form.lead_source} onChange={e => set('lead_source', e.target.value)} style={inputStyle}>
                  {LEAD_SOURCES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Priority</label>
                <select value={form.priority} onChange={e => set('priority', e.target.value)} style={inputStyle}>
                  {['low','medium','high','urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: 12 }}><label style={labelStyle}>Tags (comma-separated)</label><input value={form.tags} onChange={e => set('tags', e.target.value)} style={inputStyle} placeholder="residential, referral" /></div>
          </div>

          {/* Estimate Attachment */}
          <div style={{ background: '#fff', borderRadius: 12, padding: isMobile ? '14px 12px' : '18px 22px', marginBottom: 18, border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 10px 0' }}>Attach Existing Estimate (Optional)</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px 0' }}>Upload a PDF or photo of an existing estimate/invoice. AI will extract line items, pricing, and project details.</p>
            {!estimateFile ? (
              <label data-testid="estimate-upload-area" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '24px 16px', border: '2px dashed #d1d5db', borderRadius: 10, cursor: 'pointer', background: '#f8fafc', minHeight: 80 }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span style={{ fontSize: 13, color: '#64748b', marginTop: 6, fontWeight: 500 }}>Tap to upload PDF or image</span>
                <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>PDF, JPEG, PNG, WebP — Max 20MB</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf"
                  onChange={e => { if (e.target.files[0]) setEstimateFile(e.target.files[0]); }}
                  style={{ display: 'none' }} />
              </label>
            ) : (
              <div data-testid="estimate-file-preview" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#15803d', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{estimateFile.name}</p>
                  <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>{(estimateFile.size / 1024).toFixed(0)} KB</p>
                </div>
                <button type="button" onClick={() => setEstimateFile(null)}
                  style={{ padding: '4px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Remove</button>
              </div>
            )}
          </div>

          <button data-testid="add-lead-submit" type="submit" disabled={saving || extracting}
            style={{ width: '100%', padding: '14px', background: (saving || extracting) ? '#999' : '#c0392b', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: (saving || extracting) ? 'wait' : 'pointer', marginBottom: 40, minHeight: 48 }}>
            {extracting ? 'Analyzing Estimate...' : saving ? 'Creating Lead...' : estimateFile ? 'Add Lead & Analyze Estimate' : 'Add Lead'}
          </button>
        </form>
      </div>
    </div>
  );
}
